import { useState } from 'react';
import { ConfirmActionModal } from '@shared/components';
import { useRevokeAuthorisedContactMutation } from '../../../hooks/useRevokeAuthorisedContactMutation';
import type { AuthorisedContact } from '../../../types/authorisedContacts.types';

interface Props {
    agreementId: string;
    contact: AuthorisedContact | null;
    onClose: () => void;
}

/** A confirmation with no input, so the shared ConfirmActionModal covers it: the
 *  revoking actor and time come from the server and are recorded on the History feed.
 *  No reason is captured (A-10). */
export function RevokeAuthorisedContactDialog({ agreementId, contact, onClose }: Props) {
    const [formError, setFormError] = useState<string | null>(null);
    const mutation = useRevokeAuthorisedContactMutation(agreementId);

    const handleClose = () => {
        setFormError(null);
        onClose();
    };

    const handleConfirm = () => {
        if (!contact) return;
        setFormError(null);
        mutation.mutate(contact.id, {
            onSuccess: handleClose,
            onError: () => setFormError('Failed to revoke the authorised contact. Please try again.'),
        });
    };

    return (
        <ConfirmActionModal
            isOpen={contact !== null}
            title="Revoke authorised contact"
            message={
                <>
                    <p>
                        {contact ? `${contact.name} will no longer be authorised to speak for the plan holder.` : ''}
                    </p>
                    {formError && (
                        <p role="alert" className="mt-3 text-sm text-mp-error">
                            {formError}
                        </p>
                    )}
                </>
            }
            confirmLabel="Revoke contact"
            confirmVariant="danger"
            isPending={mutation.isPending}
            onConfirm={handleConfirm}
            onClose={handleClose}
        />
    );
}
